import {
  DailyMentionsData,
  KeyMetric,
  SourceShare,
} from 'src/pages/ThemeDetail/CampaignDataComparison/interfaces/comparison.interface'
import {
  CampaignComparisonMetric,
  CampaignComparisonResult,
} from 'src/services/campaignComparisonApi/interfaces/campaignComparison.interface'
import { KeywordTable, SentimentCampaign } from './interfaces'

/*
 * The API's per-campaign report, in the shapes the section components take.
 *
 * The report is a pair: every section of the design puts one campaign against
 * one other, so the picker asks for exactly two. The API accepts up to four,
 * and rows saved before the picker was capped can still hold more — those show
 * the first two on screen, and the downloaded deck draws every campaign.
 */

// The two campaigns a section draws.
export const PAIR = 2

// The metric that says whether there is anything to compare at all.
const TOTAL_MENTIONS = 'Total Mentions'

// What each campaign collected in the window, in the order the report names
// them. Read off the metric the report already states rather than derived a
// second way, so the check and the card can never disagree; the daily series is
// the fallback for a report whose metrics were shaped differently.
const mentionsPerCampaign = (result: CampaignComparisonResult) => {
  const metric = result.keyMetrics.find((each) => each.label === TOTAL_MENTIONS)

  if (metric) {
    return metric.values.map((value) => value.value)
  }

  return result.dailyMentions.series.map((series) =>
    series.values.reduce((sum, value) => sum + value, 0)
  )
}

// A comparison needs two sides. One campaign at zero fills nothing on its half
// of the report — no bar, no line, no keyword, no source — so every widget
// reads against an empty column, which is not a comparison however it is
// drawn. Both sides must have collected something for the report to mean
// anything.
const isComparable = (result: CampaignComparisonResult) => {
  const perCampaign = mentionsPerCampaign(result).slice(0, PAIR)

  return perCampaign.length === PAIR && perCampaign.every((total) => total > 0)
}

// The API returns raw numbers; the units belong to whatever renders them, here
// and in the PDF deck alike.
const formatMetricValue = (label: string, value: number) => {
  if (label === 'Sentiment Score') {
    return `${value}/100`
  }

  if (label === 'Active Sources') {
    return String(value)
  }

  return value.toLocaleString('en-US')
}

// A metric across the first two campaigns, as the campaign page's card reads it:
// the baseline is the "primary" side and the second campaign the "comparison".
const toKeyMetric = (metric: CampaignComparisonMetric): KeyMetric => {
  const [baseline, other] = metric.values

  return {
    comparisonPeriod: other.campaignName,
    comparisonValue: formatMetricValue(metric.label, other.value),
    // The baseline states no delta of its own, so the pill comes from the
    // second campaign — which is what the delta describes.
    delta: other.delta || '0%',
    icon: metric.icon,
    isImprovement: other.isImprovement,
    label: metric.label,
    primaryPeriod: baseline.campaignName,
    primaryValue: formatMetricValue(metric.label, baseline.value),
  }
}

export interface CampaignReportModel {
  // The two campaigns the report is of. A row saved before the picker was
  // capped can hold four; the screen shows the first two of them, the same two
  // in every section.
  campaignNames: string[]
  dailyMentions: DailyMentionsData
  insight: string
  // Whether both campaigns collected something in the window. False means at
  // least one side of every widget would be empty, so the view states that
  // instead of drawing a report against zero.
  isComparable: boolean
  keywordPeriod: string
  keywords: KeywordTable[]
  metrics: KeyMetric[]
  periodLabel: string
  sentimentCampaigns: SentimentCampaign[]
  sentimentInsight: string
  sources: SourceShare[]
}

export const toReportModel = (
  result: CampaignComparisonResult
): CampaignReportModel => {
  const [primary, comparison] = result.dailyMentions.series
  const [firstId, secondId] = result.campaigns.map(
    (campaign) => campaign.campaignId
  )

  const shareOf = (
    values: { campaignId: string; share: number }[],
    campaignId?: string
  ) => values.find((value) => value.campaignId === campaignId)?.share ?? 0

  return {
    campaignNames: result.campaigns
      .slice(0, PAIR)
      .map((campaign) => campaign.campaignName),

    dailyMentions: {
      comparison: {
        label: comparison?.campaignName || '',
        values: comparison?.values || [],
      },
      days: result.dailyMentions.days,
      primary: {
        label: primary?.campaignName || '',
        values: primary?.values || [],
      },
    },

    // The one window every campaign was measured over, as the tables' column
    // heading. Without the year, which the header already states.
    insight: result.insight,

    isComparable: isComparable(result),

    keywordPeriod: result.periodLabel.replace(/,\s*(\d{4})$/, ' $1'),

    // The same two campaigns every other section shows. Keywords could draw
    // one table per campaign, but a report that lists four keyword tables above
    // two of everything else is reading by two different rules at once.
    keywords: result.keywords.slice(0, PAIR).map((table) => ({
      campaign: table.campaignName,
      rows: table.rows.map((row) => ({
        change: row.change,
        isImprovement: row.isImprovement,
        keyword: row.keyword,
        mentions: row.mentions.toLocaleString('en-US'),
      })),
    })),

    metrics: result.keyMetrics.map(toKeyMetric),

    periodLabel: result.periodLabel,

    sentimentCampaigns: result.sentimentSplit.campaigns
      .slice(0, PAIR)
      .map((campaign) => ({
        label: campaign.campaignName,
        negative: campaign.negative,
        neutral: campaign.neutral,
        positive: campaign.positive,
      })),

    sentimentInsight: result.sentimentSplit.insight,

    sources: result.topSources.map((source) => ({
      comparison: shareOf(source.values, secondId),
      name: source.name,
      primary: shareOf(source.values, firstId),
    })),
  }
}
